import { useBunja } from 'bunja/react';
import { addMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';

import { ValidityConditionType } from '@flipster/apis/v1/rewardHub/types';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { Datetime } from '@flipster/react/design-utils/datetime';
import { css } from '@flipster/styled-system/css';
import { center, flex, stack } from '@flipster/styled-system/patterns';
import { timestampToDate } from '@flipster/utils/datetime';

import { useAuth } from '../../../shared/auth/state';
import RewardHubDefaultBanner from '../assets/images/reward-hub-default-banner.webp';
import { getLocalizedText, isDateRangeCondition, isDurationSinceSignUpCondition } from '../section-list/utils';
import { rewardHubV2Bunja } from '../state';
import { TradfiBoostBanner } from './tradfi-boost';

const getDateRange = (validityConditions?: ValidityConditionType[], signupAt?: string) => {
  const validityCondition = validityConditions?.[0];
  if (!validityCondition || validityCondition === 'alwaysAvailable') return undefined;

  if (isDateRangeCondition(validityCondition)) {
    return validityCondition.dateRange;
  }
  if (isDurationSinceSignUpCondition(validityCondition) && signupAt) {
    return {
      start: signupAt,
      end: addMilliseconds(timestampToDate(signupAt), Number(validityCondition.durationSinceSignUp) / 1_000_000),
    };
  }
  return {
    start: Date.now() * 1_000_000,
    end: addMilliseconds(Date.now(), Number(validityCondition.durationSinceSignUp) / 1_000_000),
  };
};

const RewardHubImageBanner = () => {
  const { user } = useAuth();
  const locale = useSupportedLocale();

  const { specAtom, skinAtom } = useBunja(rewardHubV2Bunja);
  const skin = useAtomValue(skinAtom);
  const validityConditions = useAtomValue(specAtom)?.validityConditions;

  const title = getLocalizedText(skin?.description.head?.localizationTable, locale);
  const bannerImage = getLocalizedText(skin?.description.banner?.localizationTable, locale);
  const dateRange = getDateRange(validityConditions, user?.createdAt);

  return (
    <section
      className={flex({
        pt: 16,
        pb: { base: 24, smallDesktop: 0 },
        px: { base: 16, smallDesktop: 0 },
        flexDirection: { base: 'column', smallDesktop: 'row' },
        align: { base: 'start', smallDesktop: 'start' },
        justify: 'space-between',
        gap: { base: 24, smallDesktop: 40 },
      })}
    >
      <div className={stack({ gap: { base: 16, smallDesktop: 24 }, pt: { base: 0, smallDesktop: 64 } })}>
        <p
          className={css({
            textStyle: 'Display/XL',
            color: 'content.default.level-5',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          })}
        >
          {title}
        </p>
        {dateRange && (
          <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-2' })}>
            <Datetime value={dateRange.start} format='MMM dd, yyyy' /> ~{' '}
            <Datetime value={dateRange.end} format='MMM dd, yyyy' />
          </p>
        )}
        {/* desktop */}
        <TradfiBoostBanner css={{ display: { base: 'none', smallDesktop: 'block' }, w: 'fit-content' }} />
      </div>
      <div
        className={center({
          alignSelf: 'center',
          w: { base: 180, smallDesktop: 320 },
          h: { base: 180, smallDesktop: 320 },
        })}
      >
        <img
          src={bannerImage || RewardHubDefaultBanner.src}
          alt='Reward Hub Banner'
          onError={(e) => {
            e.currentTarget.src = RewardHubDefaultBanner.src;
          }}
          width='100%'
          className={css({ objectFit: 'contain' })}
        />
      </div>
      {/* mobile */}
      <TradfiBoostBanner css={{ display: { base: 'block', smallDesktop: 'none' }, w: '100%' }} />
    </section>
  );
};

export default RewardHubImageBanner;
